import {useTranslation} from '@/translations/i18n.tsx'
import {CategoryCard} from './category-card'
import {Shield02Icon} from '@components/ui/icon'
import {useMe} from '@features/user/queries/useMe'
import {raise} from '@utilities/helpers'
import {Button} from '@components/ui/button'
import {useState} from 'react'
import {ChangePasswordModal} from '@features/settings/components/change-password-modal'
import {ChangeEmailModal} from '@features/settings/components/change-email-modal'
import {useDeleteNewEmailRequest} from '@features/settings/queries/useDeleteNewEmailRequest'
import {useResendConfirmationEmail} from '@features/settings/queries/useResendConfirmationEmail'
import {Badge} from '@components/ui/badge'
import {toast} from '@utilities/toast'
import {SETTINGS_SECTION_WRAPPER_CLASSNAME} from '@features/settings/constants.ts'

const EMAIL_SECTION_CLASSNAME =
    'flex flex-col items-start gap-1.5 text-md [&_p]:text-neutral-700 [&_span]:font-medium [&_small]:text-neutral-700 [&_small]:text-sm'

export const AccountSecurity = () => {
    const {t} = useTranslation()
    const userMeQuery = useMe({throwOnError: true})
    const user = userMeQuery.data ?? raise('user is nullish')
    const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false)
    const [isChangeEmailModalOpen, setIsChangeEmailModalOpen] = useState(false)

    const onSuccessCancelEmailChange = () => {
        toast.success(t('settings:personal_information:change_email_request_deleted'))
    }
    const onSuccessResendConfirmationEmail = () => {
        toast.success(
            <div className="text-sm [&_h3]:font-medium [&_p]:text-neutral-700">
                <h3>{t('settings:personal_information:verification_email_sent')}</h3>
                <p>{t('settings:personal_information:check_spam')}</p>
            </div>
        )
    }
    const cancelEmailChangeMutation = useDeleteNewEmailRequest({onSuccess: onSuccessCancelEmailChange})
    const resendConfirmationEmailMutation = useResendConfirmationEmail({onSuccess: onSuccessResendConfirmationEmail})
    const isPendingConfirmationEmail = !!user.newEmail

    return (
        <div className={SETTINGS_SECTION_WRAPPER_CLASSNAME}>
            <CategoryCard
                icon={Shield02Icon}
                title={t('settings:personal_information:security_label')}
                description={t('settings:personal_information:security_description')}
            />
            <div className="flex flex-col gap-4">
                {isPendingConfirmationEmail ? (
                    <div className={EMAIL_SECTION_CLASSNAME}>
                        <p>{t('commons:email')}</p>
                        <div className="flex flex-wrap gap-2">
                            <span>{user.newEmail}</span>

                            <Badge variant="orange" shape="rectangle">
                                {t('settings:personal_information:pending_confirmation')}
                            </Badge>
                        </div>
                        <div className="flex flex-col gap-4">
                            <small>{t('settings:personal_information:current_email', {email: user.email})}</small>
                            <div className="flex flex-wrap items-center gap-4">
                                <Button
                                    variant="secondary"
                                    onClick={() => resendConfirmationEmailMutation.mutate()}
                                    disabled={
                                        cancelEmailChangeMutation.isPending || resendConfirmationEmailMutation.isPending
                                    }
                                >
                                    {t('settings:personal_information:resend_email')}
                                </Button>

                                <Button
                                    variant="tertiary"
                                    shape="text"
                                    onClick={() => cancelEmailChangeMutation.mutate()}
                                    disabled={
                                        cancelEmailChangeMutation.isPending || resendConfirmationEmailMutation.isPending
                                    }
                                >
                                    {t('settings:personal_information:cancel_email_confirmation')}
                                </Button>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className={EMAIL_SECTION_CLASSNAME}>
                        <p>{t('commons:email')}</p>

                        <span>{user.email}</span>

                        <Button
                            variant="tertiary"
                            shape="text"
                            size="lg"
                            onClick={() => setIsChangeEmailModalOpen(true)}
                        >
                            {t('commons:change')}
                        </Button>
                    </div>
                )}

                <div className={EMAIL_SECTION_CLASSNAME}>
                    <p>{t('commons:password')}</p>
                    <Button
                        variant="tertiary"
                        shape="text"
                        size="lg"
                        onClick={() => setIsChangePasswordModalOpen(true)}
                    >
                        {t('commons:change')}
                    </Button>
                </div>
            </div>

            {isChangePasswordModalOpen && <ChangePasswordModal onClose={() => setIsChangePasswordModalOpen(false)} />}
            {isChangeEmailModalOpen && <ChangeEmailModal onClose={() => setIsChangeEmailModalOpen(false)} />}
        </div>
    )
}
